import * as React from "react";
import { formatMessage } from "lib/globalize-util";
import { Map } from "immutable";
import classNames from "classnames";
import debounce from "lodash/debounce";

interface IFieldProps {
    value: string;
    field: Map<string, any>;
    readOnly: boolean;
    onChange: any;
    onChangeFocus: any;
    onKeyDown: (e /*KeyboardEvent<HTMLDivElement>*/) => void;
}

interface IState {
    errorMessage?: string;
    focus: boolean;
    displayedValue: string;
}

export class NumberField extends React.PureComponent<IFieldProps, IState> {
    private editor;

    constructor(props) {
        super(props);
        this.state = {
            errorMessage: "",
            focus: false,
            displayedValue: this.formatValue(props.value, props),
        };
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        // Do not update if typing
        if (!this.state.focus) {
            this.setState({
                displayedValue: this.formatValue(nextProps.value, nextProps),
            });
        }
    }

    render() {
        const { field, readOnly, onKeyDown } = this.props;
        const { errorMessage, focus, displayedValue } = this.state;
        const value = displayedValue;
        const valueType = field.get("value_type");
        let additionalLabel = "";
        const numberFormat = field.get("number_format");
        if (numberFormat) {
            let symbol = "";
            const currency = numberFormat.get("currency");
            const percentage = numberFormat.get("percentage");
            if (currency) {
                symbol = currency.get("symbol");
            } else if (percentage) {
                symbol = "%";
            }
            if (value && symbol) {
                additionalLabel = `(${symbol})`;
            }
        }

        return (
            <div
                className={classNames("fieldContainer", {
                    isFocused: focus,
                    editable: !readOnly,
                    error: !!errorMessage,
                })}
                onClick={this.handleClick}
            >
                <span className="fieldLabel">
                    {field.get("display_name")} {additionalLabel}
                </span>
                {!readOnly && (
                    <div className="inputContainer" onKeyDown={onKeyDown}>
                        <input
                            data-test={field.get("field_name")}
                            ref={this.bindEditor}
                            name={field.get("field_name")}
                            placeholder={this.getPlaceholderText()}
                            type="text"
                            value={value}
                            onChange={this.handleChange}
                            onFocus={this.handleFocus}
                            onBlur={this.handleBlur}
                            autoComplete="off"
                        />
                        {errorMessage && <div className="errorMessage">{errorMessage}</div>}
                    </div>
                )}
                {readOnly && <span className="fieldValue nonEditable">{value}</span>}
            </div>
        );
    }

    private formatValue = (value, props = this.props) => {
        const { field } = props;
        if (!value) {
            return "";
        }

        let decimalPlaces = 0;
        const numberFormat = field.get("number_format");
        if (numberFormat) {
            const currency = numberFormat.get("currency");
            const percentage = numberFormat.get("percentage");
            if (currency) {
                decimalPlaces = currency.get("decimal_places");
            } else if (percentage) {
                decimalPlaces = percentage.get("decimal_places");
            }
        }

        const formattedValue = Number(value).toFixed(decimalPlaces);
        if (formattedValue === "NaN") {
            return "";
        }
        return formattedValue;
    };

    private bindEditor = (ref) => (this.editor = ref);

    private getPlaceholderText = () => {
        return formatMessage("Enter only numbers");
    };

    private handleClick = () => {
        process.nextTick(() => {
            if (this.editor && !this.state.focus) {
                this.editor.focus();
            }
        });
    };

    private handleFocus = () => {
        this.setState({ focus: true, errorMessage: "" });
    };

    private handleBlur = () => {
        this.setState({ focus: false, errorMessage: "" });
        this.handleSaveChanges.cancel();
        const { field, onChange, value = "" } = this.props;
        const { displayedValue } = this.state;
        if (value !== displayedValue) {
            onChange({ value: displayedValue, field });
        }
    };

    private isNegativeValue = (value) => {
        if (value === "") {
            return false;
        }

        const input = Number(value);
        return isNaN(input) ? false : Math.abs(input) !== input;
    };

    private handleChange = (event) => {
        const { field, onChange } = this.props;
        const valueType = field.get("value_type");
        if (event.target) {
            let decimalPlaces = 0;
            const numberFormat = field.get("number_format");
            if (numberFormat && numberFormat.get("currency")) {
                decimalPlaces = numberFormat.getIn(["currency", "decimal_places"]);
            } else if (numberFormat && numberFormat.get("percentage")) {
                decimalPlaces = numberFormat.getIn(["percentage", "decimal_places"]);
            }
            // eslint-disable-next-line no-useless-escape
            const regex =
                decimalPlaces === 0 ? new RegExp("^\\d+$") : new RegExp(`^\\d+(\\.\\d{0,${decimalPlaces}})?$`);
            const inputValue = this.isNegativeValue(event.target.value) ? "" : event.target.value;
            if (inputValue === "" || regex.test(inputValue)) {
                this.setState({
                    displayedValue: inputValue,
                    errorMessage: "",
                });
                this.handleSaveChanges({ value: inputValue, field });
            } else {
                this.setState({ errorMessage: formatMessage("Please enter only numbers") });
            }
        }
    };

    // tslint:disable-next-line: member-ordering
    private handleSaveChanges = debounce(({ value, field }) => this.props.onChange({ value, field }), 600);
}
